import type {
  CalculatorData,
  CalculatorOption,
  LeasingOwGroup,
  RentOwGroup
} from "../types/calculatorData";

export interface CalculatorRadioChangeEvent {
  Parent?: string;
  RadioName: string;
  Value: number;
  Status: boolean;
}

type RentOrLeasingParent = "RentPeriods" | "LeasingPeriods";

function getParentKey(parent?: string): RentOrLeasingParent {
  return parent === "LongTimeRentCalculatorValuesDTO" ? "RentPeriods" : "LeasingPeriods";
}

function getExtensionOptions(
  parent: RentOrLeasingParent,
  ow: RentOwGroup | LeasingOwGroup
): CalculatorOption[] {
  return parent === "RentPeriods"
    ? (ow as RentOwGroup).Mileages
    : (ow as LeasingOwGroup).Rvs;
}

function rewriteOwsOptions(
  event: CalculatorRadioChangeEvent | null,
  parent: RentOrLeasingParent,
  rawCalcOptions: CalculatorData,
  oldOw: RentOwGroup | LeasingOwGroup,
  rewriteExtByName?: boolean
) {
  const oldExtOptions = getExtensionOptions(parent, oldOw);
  const oldSelectedExt = oldExtOptions.find((x) => x.Status === true)!;
  oldOw.Ow.Status = false;

  const activePeriod = rawCalcOptions[parent].find((x) => x.Period.Status === true)!;
  const oldSelectedOw = activePeriod.Ows.find((x) => x.Ow.Status === true);
  if (oldSelectedOw) {
    oldSelectedOw.Ow.Status = false;
  }

  const eventValue = event ? event.Value : oldOw.Ow.Value;
  const newSelectedPeriod = rawCalcOptions[parent].find((x) => x.Period.Status === true)!;
  let newSelectedOw = newSelectedPeriod.Ows.find((x) => x.Ow.Value === eventValue);
  if (!newSelectedOw) {
    newSelectedOw = newSelectedPeriod.Ows[0];
  }

  newSelectedOw.Ow.Status = true;

  const extOptions = getExtensionOptions(parent, newSelectedOw);
  const currentlySelected = extOptions.find((x) => x.Status === true);
  if (currentlySelected) {
    currentlySelected.Status = false;
  }

  if (rewriteExtByName) {
    const newSelectedExt = extOptions.find((x) => oldSelectedExt.Name === x.Name);
    if (newSelectedExt) {
      newSelectedExt.Status = true;
    } else {
      extOptions[0].Status = true;
    }
  } else {
    const newSelectedExt = extOptions.find((x) => oldSelectedExt.Value === x.Value);
    if (newSelectedExt) {
      newSelectedExt.Status = true;
    } else {
      extOptions[0].Status = true;
    }
  }
}

/**
 * Updates RentPeriods / LeasingPeriods Status flags (legacy Calculator.handleRadioBtnChange).
 */
export function applyCalculatorRadioChange(
  rawCalcOptions: CalculatorData,
  event: CalculatorRadioChangeEvent
): CalculatorData {
  const parent = getParentKey(event.Parent);

  switch (event.RadioName) {
    case "Periods": {
      const oldSelectedPeriod = rawCalcOptions[parent].find((x) => x.Period.Status === true)!;
      oldSelectedPeriod.Period.Status = false;

      const oldSelectedOw = oldSelectedPeriod.Ows.find((x) => x.Ow.Status === true)!;

      const newSelectedPeriod = rawCalcOptions[parent].find((x) => x.Period.Value === event.Value)!;
      newSelectedPeriod.Period.Status = true;

      rewriteOwsOptions(null, parent, rawCalcOptions, oldSelectedOw, true);
      break;
    }
    case "Ows": {
      const oldOw = rawCalcOptions[parent]
        .find((x) => x.Period.Status === true)!
        .Ows.find((x) => x.Ow.Status === true)!;
      rewriteOwsOptions(event, parent, rawCalcOptions, oldOw);
      break;
    }
    case "Mileages": {
      const mileages = (rawCalcOptions.RentPeriods
        .find((x) => x.Period.Status === true)!
        .Ows.find((x) => x.Ow.Status === true)! as RentOwGroup).Mileages;
      mileages.find((x) => x.Status === true)!.Status = false;
      mileages.find((x) => x.Value === event.Value)!.Status = true;
      break;
    }
    case "Rvs": {
      const rvs = (rawCalcOptions.LeasingPeriods
        .find((x) => x.Period.Status === true)!
        .Ows.find((x) => x.Ow.Status === true)! as LeasingOwGroup).Rvs;
      rvs.find((x) => x.Status === true)!.Status = false;
      rvs.find((x) => x.Value === event.Value)!.Status = true;
      break;
    }
    default:
      console.error("Calculator radio event not found:", event.RadioName);
  }

  return rawCalcOptions;
}
